import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { refusalText } from "../../lib/refusal.js";
import type {
  ClipCue,
  GameRules,
  SideLabel,
  Snapshot,
} from "../../services/generated/client.js";
import { connectToGame, type GameLink } from "../../services/gameLink.js";
import { HttpError } from "../../services/http.js";
import { joinParty } from "../../services/partyService.js";
import { realtimeUrl } from "../../services/realtime.js";

/**
 * The display's connection to a party.
 *
 * # Why this is not `pages/party/useGame.ts`
 *
 * ADR 0005: `/display` ships as a Tizen and webOS app in phase 10, and that is
 * only cheap while it stays a self-contained page — so it must not import the
 * player shell. About forty lines are duplicated here as a result, and that is
 * the right trade: this version is genuinely different (no private topic, no
 * intents, no name to type), and the alternative is one shared hook growing a
 * `role` parameter and dragging the player's concerns into the television's
 * bundle.
 *
 * # A display joins itself, two ways
 *
 * There is no form. A television has no keyboard worth using, and asking for one
 * is how the display quietly becomes "the host's laptop" again.
 *
 * - **In a browser** (`/display/BKGZ`), opening the URL *is* the join: no seat
 *   means join as a display, then connect. The cookie the server sets is the
 *   seat and script never sees it.
 * - **In a packaged app** (phase 11c), there is no URL to open and no cookie
 *   that would be sent, so the seat arrives from a pairing the host claimed and
 *   the television holds the token itself.
 *
 * Everything after the seat is identical, which is the point — one board, one
 * connection, one set of topics.
 */
export type BoardSeat =
  /** A browser at `/display/:code`: join by code, hold a cookie. */
  | { kind: "room"; code: string }
  /** A paired television: present the token it was given. */
  | { kind: "paired"; code: string; token: string };

export interface Board {
  /** Where the connection is. */
  status: "connecting" | "connected" | "failed";
  /** In words, when `status` is `failed`. */
  error: string | null;
  /** Whether a game is running. */
  live: boolean;
  /** The board — exactly `snapshot(game, Display)`, the most restricted view. */
  board: Snapshot | null;
  /** Prices and `cards_to_win`, for the progress readout. */
  rules: GameRules | null;
  /**
   * Who each side is.
   *
   * The television needed this as much as the phone did and never had it: until
   * phase 7 the board wrote "Playing now" over the active side and a blank over
   * everybody else, which works only because the people it names are in the
   * room.
   */
  roster: SideLabel[];
  /**
   * Which game, for the recap link at the end.
   *
   * `null` before one starts. `watch` reads it from the live row, so a
   * television opened after the final settlement has none — which is correct:
   * the screen that was there is the one that can hand the link out.
   */
  gameId: string | null;
  /** The last clip cue. In couch mode this screen is the audio sink. */
  cue: ClipCue | null;
  /**
   * When the phase in flight runs out, on **this television's** clock.
   *
   * `null` when nothing is timing it. The server sends a duration and
   * `gameLink` anchors it here, which is what stops a box whose clock nobody
   * ever set from showing every round as already over.
   */
  phaseEndsAt: number | null;
  /** How long that phase gets in total, for the bar. */
  phaseSeconds: number | null;
  /**
   * The seat itself was refused, rather than the connection failing.
   *
   * Only a paired television can be in this state, and it is the one failure a
   * retry cannot fix: the party has closed, or the token was superseded. It is
   * separate from `error` because the answer is different — **pair again**,
   * which is something a television can do on its own, where "try again" leaves
   * it stuck on a screen nobody is standing at.
   */
  seatLost: boolean;
  /** Try again. */
  reconnect: () => void;
}

export function useBoard(seat: BoardSeat): Board {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Board["status"]>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const [board, setBoard] = useState<Snapshot | null>(null);
  const [rules, setRules] = useState<GameRules | null>(null);
  const [roster, setRoster] = useState<SideLabel[]>([]);
  const [gameId, setGameId] = useState<string | null>(null);
  const [cue, setCue] = useState<ClipCue | null>(null);
  const [phaseEndsAt, setPhaseEndsAt] = useState<number | null>(null);
  const [phaseSeconds, setPhaseSeconds] = useState<number | null>(null);
  const [seatLost, setSeatLost] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const reconnect = useCallback(() => {
    setStatus("connecting");
    setError(null);
    setSeatLost(false);
    setAttempt((n) => n + 1);
  }, []);

  // The effect depends on the seat's *contents*, not on the object: a caller
  // that rebuilds `{kind, code, token}` every render would otherwise reconnect
  // on every render, which on a television is a socket storm nobody is watching.
  const kind = seat.kind;
  const code = seat.code;
  const token = seat.kind === "paired" ? seat.token : null;

  useEffect(() => {
    let cancelled = false;
    let opened: GameLink | null = null;

    const open = async (): Promise<GameLink> =>
      connectToGame(
        realtimeUrl(),
        {
          onState: (view) => {
            if (cancelled) {
              return;
            }
            setLive(view.live);
            setBoard(view.board);
            setRules(view.rules);
            setRoster(view.roster);
            setPhaseEndsAt(view.phaseEndsAt);
            setPhaseSeconds(view.phaseSeconds);
            // Kept once known: the id disappears from `watch` the moment the game
            // finishes, which is exactly when the board wants to show its recap.
            setGameId((held) => view.gameId ?? held);
          },
          onClip: (next) => {
            if (!cancelled) {
              setCue(next);
            }
          },
        },
        token ?? undefined,
      );

    void (async () => {
      try {
        let connection: GameLink;
        try {
          connection = await open();
        } catch (cause) {
          if (!(cause instanceof HttpError) || cause.status !== 401) {
            throw cause;
          }
          if (kind === "paired") {
            // The token is dead: the party closed, or another pairing
            // superseded it. Retrying cannot fix either, and a television with
            // nobody standing at it needs a way back that is not a button — so
            // this is reported as a *lost seat* and the page pairs again.
            if (!cancelled) {
              setStatus("failed");
              setSeatLost(true);
              setError(t("tv.seatLost"));
            }
            return;
          }
          // No seat yet. A display's answer to that is to take one — there is
          // nobody at the television to fill in a form. It sends no name
          // either: `party::DISPLAY_NAME` is the server's answer to that, and
          // the route refusing to supply one is the bug 11c found.
          await joinParty({ code, as_display: true });
          connection = await open();
        }

        if (cancelled) {
          connection.disconnect();
          return;
        }
        opened = connection;
        setStatus("connected");
      } catch (cause) {
        if (!cancelled) {
          setStatus("failed");
          setError(refusalText(cause, t));
        }
      }
    })();

    return () => {
      cancelled = true;
      opened?.disconnect();
    };
    // `t` is a dependency because the effect words its own failures. It changes
    // only on a language switch, and a television reconnecting then is fine —
    // the seat is held in storage, not in this closure.
  }, [attempt, kind, code, token, t]);

  return {
    status,
    error,
    live,
    board,
    rules,
    roster,
    gameId,
    cue,
    phaseEndsAt,
    phaseSeconds,
    seatLost,
    reconnect,
  };
}
